// Risk Deadlines Component
// Displays upcoming risk deadlines with scrollable list and infinite scroll

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { RiskService } from '../../services/riskService';

const PAGE_SIZE = 15;
const SCROLL_THRESHOLD = 80;

interface RiskDeadline {
  risk_id: string;
  risk_title: string;
  owner_name: string;
  due_date: string;
}

export const RiskDeadlines: React.FC = () => {
  const { t } = useLanguage();
  const [deadlines, setDeadlines] = useState<RiskDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextPageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const riskService = new RiskService();

  const loadPage = useCallback(async (pageNum: number, append: boolean) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const data = await riskService.getUpcomingDeadlines(pageNum, PAGE_SIZE);
      setDeadlines(prev => append ? [...prev, ...data] : data);
      setHasMore(data.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Error loading upcoming deadlines:', err);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
      setLoadingMore(true);
      nextPageRef.current += 1;
      loadPage(nextPageRef.current, true);
    }
  }, [loadingMore, hasMore, loadPage]);

  return (
    <div className="border border-[var(--color-border)] rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-4">
          {t('risk.dashboard.upcomingDeadlines')}
        </h3>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flow-root max-h-80 overflow-y-auto"
        >
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]" />
            </div>
          ) : (
            <ul className="-mb-8">
              {deadlines.map((deadline, deadlineIdx) => (
                <li key={deadline.risk_id}>
                  <div className="relative pb-8">
                    {deadlineIdx !== deadlines.length - 1 ? (
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-[var(--color-border)]" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center ring-8 ring-transparent">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-[var(--color-secondary-text)]">
                            <span className="text-[var(--color-surface-text)]">{deadline.risk_title}</span> due{' '}
                            <span className="text-[var(--color-surface-text)]">{deadline.owner_name}</span>
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-[var(--color-secondary-text)]">
                          <time dateTime={deadline.due_date}>
                            {new Date(deadline.due_date).toLocaleDateString()}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              {deadlines.length === 0 && (
                <li>
                  <div className="text-center py-8 text-[var(--color-secondary-text)]">
                    <p>{t('risk.dashboard.noUpcomingDeadlines')}</p>
                  </div>
                </li>
              )}
              {loadingMore && (
                <li className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-bg)]" />
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
